import type {
  EditorPresence,
  InstallationSettings as WebAppInstallationSettings,
  UnifiedLightbox,
} from '@wix/ambassador-devcenter-v1-component/types';
import {
  InstallPage,
  ComponentType,
} from '@wix/ambassador-devcenter-v1-component/types';
import type {
  BlocksAppDescriptor,
  ComponentInCsm,
} from '@wix/document-services-types';
import type {
  ExtendedBlocksAppData,
  StudioWidgetComponent,
} from '../types/ExtendedBlocksAppData';
import type { ITranslations } from '../services/Translations/ITranslations';
import type { ExtendedAppManifest } from './manifestUtils';
import { removeHash } from './removeHash';
import { DEFAULT_WIDGET_WIDTH, DEFAULT_WIDGET_HEIGHT } from '../constants';

export interface InstallationSettings extends WebAppInstallationSettings {
  isStretched?: boolean;
  isUnified: boolean;
  shouldAddAsLightbox: boolean;
  showInAddPanel?: boolean; // Override to ensure it's boolean | undefined, not null
}

export type InstallationSettingsById = Record<
  string,
  InstallationSettings | undefined
>;

export type EditorPresenceById = Record<string, EditorPresence | undefined>;

export const isStudioWidgetComponent = (
  comp: ComponentInCsm | StudioWidgetComponent,
): comp is StudioWidgetComponent => comp.type === ComponentType.STUDIO_WIDGET;

export const getInstallationSettingsByWidgetId = ({
  components,
}: ExtendedBlocksAppData) => {
  const lightboxComponentsData = components
    .filter((component) => component.type === ComponentType.UNIFIED_LIGHTBOX)
    .map((lightboxComponent) => lightboxComponent?.data as UnifiedLightbox);

  return components
    .filter(isStudioWidgetComponent)
    .reduce<InstallationSettingsById>((acc, { data, componentId }) => {
      const currentLightboxId =
        data?.installationSettings?.lightboxOptions?.lightboxId;

      const isMigratedToLightboxExtension = lightboxComponentsData.some(
        (lightboxData) =>
          lightboxData?.base?.id === currentLightboxId ||
          lightboxData?.content?.widgets?.some(
            ({ widgetGuid }) => widgetGuid === componentId,
          ),
      );

      const shouldAddAsLightbox =
        !isMigratedToLightboxExtension &&
        data.installationSettings?.installPage === InstallPage.LIGHTBOX;

      if (data.studioWidgetId) {
        acc[data.studioWidgetId] = {
          ...data.installationSettings,
          showInAddPanel:
            data.installationSettings?.showInAddPanel === null
              ? undefined
              : data.installationSettings?.showInAddPanel,
          isStretched: data.isStretched?.desktop ?? undefined,
          isUnified: !!data.installation?.base,
          shouldAddAsLightbox,
        };
      }
      return acc;
    }, {});
};

export const getEditorPresenceByWidgetId = ({
  components,
}: ExtendedBlocksAppData) =>
  components
    .filter(isStudioWidgetComponent)
    .reduce<EditorPresenceById>((acc, { data }) => {
      if (data.studioWidgetId) {
        acc[data.studioWidgetId] = data.editorPresence;
      }
      return acc;
    }, {});

export const getInstallationSettings = (
  widgetPageId: string,
  appData: ExtendedBlocksAppData,
) => getInstallationSettingsByWidgetId(appData)[widgetPageId];

export const appAddsPages = ({ components }: ExtendedBlocksAppData) =>
  components.some(
    (component) =>
      isStudioWidgetComponent(component) &&
      component.data.installationSettings?.installPage &&
      [InstallPage.PAGE, InstallPage.LIGHTBOX].includes(
        component.data.installationSettings.installPage,
      ),
  );

export const getPagesManifest = (
  isResponsive: boolean,
  translations: ITranslations,
  isFixPagesMenuTabsExperimentEnabled: boolean,
) =>
  ({
    pages: {
      pageActions: {
        default: [
          ...(isResponsive
            ? []
            : [
                {
                  type: 'page_rename' as const,
                  title: translations.translate('Rename'),
                },
              ]),
        ],
      },
      pageSettings: {
        default: [
          { type: 'seo' as const, title: translations.translate('SEO') },
          ...(isFixPagesMenuTabsExperimentEnabled
            ? [
                {
                  type: 'layout' as const,
                  title: translations.translate('Layout'),
                },
                {
                  type: 'page_info' as const,
                  title: translations.translate('Page Info'),
                },
              ]
            : []),
        ],
      },
    },
  } satisfies Pick<ExtendedAppManifest, 'pages'>);

export const getMobilePresetFromEditorPresence = async (
  desktopPresetId: string,
  widgetPageId: string,
  appData: ExtendedBlocksAppData,
  appDescriptor: BlocksAppDescriptor,
) => {
  const mobilePresetId = getEditorPresenceByWidgetId(appData)[
    widgetPageId
  ]?.presetsEditorPresence?.find(
    (presetEditorPresence) => presetEditorPresence.presetId === desktopPresetId,
  )?.mobilePresetId;

  if (!mobilePresetId) {
    return undefined;
  }

  const widgetDescriptor = appDescriptor.widgets[widgetPageId];
  const presetInfo = widgetDescriptor.presets?.find(
    (preset) => removeHash(preset.presetId) === mobilePresetId,
  );

  return {
    id: mobilePresetId,
    size: {
      width: presetInfo?.defaultSize?.width?.value || DEFAULT_WIDGET_WIDTH,
      height: presetInfo?.defaultSize?.height?.value || DEFAULT_WIDGET_HEIGHT,
    },
  };
};
